import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { Modal, useToast } from '@/components/ui';
import { changePassword, ProfileApiError } from './profileApi';
import { FormField } from './FormField';

export interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
}

const MIN_LENGTH = 8; // MePasswordChangeSerializer.new_password min_length=8

/**
 * EAA-1822: "Đổi mật khẩu" trigger on frame 18's identity card. The frame only
 * shows the button, not the dialog itself, so field layout here follows the
 * repo's existing modal-form convention (see `CreateOrganizationModal`) rather
 * than a captured frame — flagged in the EAA-1822 report.
 *
 * Wired to the REAL `POST /api/v1/me/password` (portal-api EAA-1830):
 * current_password is verified against the realm before anything changes,
 * and a wrong one comes back as a translated inline error under that field
 * (never a toast alone — same rule `CreateOrganizationModal` follows for a
 * duplicate org code).
 */
export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [attempted, setAttempted] = useState(false);
    const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);

    const showError = (field: string) => attempted || touched[field];

    const currentPasswordInvalid = showError('current') && currentPassword.length === 0;
    const newPasswordInvalid = showError('new') && newPassword.length < MIN_LENGTH;
    const confirmInvalid = showError('confirm') && confirmPassword !== newPassword;

    const canSubmit =
        currentPassword.length > 0 &&
        newPassword.length >= MIN_LENGTH &&
        confirmPassword === newPassword;

    const reset = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTouched({});
        setAttempted(false);
        setCurrentPasswordError(null);
    };

    const mutation = useMutation({
        mutationFn: () =>
            changePassword(config.apiBase, accessToken as string, {
                current_password: currentPassword,
                new_password: newPassword,
            }),
        onSuccess: () => {
            reset();
            onClose();
            toast.success(t('profile.changePasswordModal.successToast'));
        },
        onError: (error) => {
            // AUTH_INVALID_CREDENTIALS (wrong current password) — an inline
            // field error, not a toast, mirroring the duplicate-code pattern.
            if (error instanceof ProfileApiError) {
                setCurrentPasswordError(error.message);
                return;
            }
            toast.error(t('profile.changePasswordModal.errorToast'));
        },
    });

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        setAttempted(true);
        setCurrentPasswordError(null);
        if (!canSubmit) return;
        mutation.mutate();
    };

    const handleClose = () => {
        if (mutation.isPending) return;
        reset();
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={t('profile.changePassword')}
            width={440}
            footer={
                <>
                    <button
                        type="button"
                        className="portal-btn"
                        onClick={handleClose}
                        disabled={mutation.isPending}
                    >
                        {/* EAA-1845: verbatim original spelling "Hủy" (tone mark on the u) —
                            deliberately NOT reused via common.cancel, whose catalog value is
                            the other valid spelling "Huỷ" (tone mark on the y); merging them
                            would silently change on-screen text this ticket must not reword. */}
                        {t('profile.changePasswordModal.cancel')}
                    </button>
                    <button
                        type="submit"
                        form="change-password-form"
                        className="portal-btn portal-btn-primary"
                        disabled={!canSubmit || mutation.isPending}
                    >
                        {mutation.isPending
                            ? t('profile.changingPassword')
                            : t('profile.changePassword')}
                    </button>
                </>
            }
        >
            <form
                id="change-password-form"
                className="portal-profile-password-form"
                onSubmit={onSubmit}
            >
                <FormField
                    label={t('profile.changePasswordModal.currentPasswordLabel')}
                    required
                    error={
                        currentPasswordError ??
                        (currentPasswordInvalid
                            ? t('profile.changePasswordModal.currentPasswordRequired')
                            : null)
                    }
                >
                    <input
                        type="password"
                        className="portal-input"
                        value={currentPassword}
                        onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setCurrentPasswordError(null);
                        }}
                        onBlur={() => setTouched((t) => ({ ...t, current: true }))}
                        autoFocus
                        autoComplete="current-password"
                    />
                </FormField>
                <FormField
                    label={t('profile.changePasswordModal.newPasswordLabel')}
                    required
                    helper={t('profile.changePasswordModal.minLengthHelper', { min: MIN_LENGTH })}
                    error={
                        newPasswordInvalid
                            ? t('profile.changePasswordModal.newPasswordTooShort', { min: MIN_LENGTH })
                            : null
                    }
                >
                    <input
                        type="password"
                        className="portal-input"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, new: true }))}
                        autoComplete="new-password"
                    />
                </FormField>
                <FormField
                    label={t('profile.changePasswordModal.confirmPasswordLabel')}
                    required
                    error={confirmInvalid ? t('profile.changePasswordModal.passwordMismatch') : null}
                >
                    <input
                        type="password"
                        className="portal-input"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                        autoComplete="new-password"
                    />
                </FormField>
            </form>
        </Modal>
    );
}

export default ChangePasswordModal;
